<template>
    <div class="vat-settings">
        <div class="vat-settings__columns">
            <Card>
                <div class="vat-settings__card-header">
                    <Heading
                        :labelTitle="$t.__('settings.taxes.VatSettings.vat.title')"
                        variant="h2"
                        :prefixColoredIcon="{ icon: 'percent', color: 'brand' }"
                    />
                    <CompassButton
                        v-if="!isLoading"
                        :label="$t.__('settings.taxes.VatSettings.vat.button')"
                        type="secondary"
                        leftIcon="settings"
                        @click="openVatRateDialog"
                    />
                </div>
                <VatSettingsLoader v-if="isLoading" />
                <template v-else>
                    <CompassFormItem
                        :label="$t.__('settings.taxes.VatSettings.vat.type.label')"
                        :info="$t.__('settings.taxes.VatSettings.vat.type.info')"
                        class="vat-settings__form-item"
                    >
                        <CompassRadioGroup
                            v-model="taxesSettings.vat_mode"
                            label=""
                            direction="horizontal"
                            @input="handleSettingsUpdate"
                        >
                            <CompassRadio
                                v-for="item in vatModes"
                                :key="item.value"
                                :modelValue="item.value"
                                :label="item.label"
                            />
                        </CompassRadioGroup>
                    </CompassFormItem>
                    <div class="vat-settings__form-item--bordered" v-if="!forceVatMention()">
                        <CompassSwitch
                            v-model="taxesSettings.display_tax_mention"
                            class="vat-settings__switch"
                            orientation="left"
                            :label="
                                $t.__('settings.taxes.VatSettings.vat.type.display_tax_mention')
                            "
                            @change="handleSettingsUpdate"
                        />
                        <CompassFormItem
                            v-if="taxesSettings.display_tax_mention"
                            :label="$t.__('settings.taxes.VatSettings.vat.type.sales_docs.label')"
                            :info="$t.__('settings.taxes.VatSettings.vat.type.sales_docs.info')"
                        >
                            <CompassInput
                                v-model="taxesSettings.vat_mention"
                                maxlength="255"
                                @update:modelValue="handleSettingsUpdate"
                            />
                        </CompassFormItem>
                    </div>
                    <CompassFormItem
                        :label="$t.__('settings.taxes.VatSettings.vat.default_vat.label')"
                        :info="$t.__('settings.taxes.VatSettings.vat.default_vat.info')"
                    >
                        <CompassSelect
                            v-model="taxesSettings.vat_id"
                            class="vat-settings__select"
                            @update:modelValue="handleSettingsUpdate"
                        >
                            <CompassDropdownOption
                                v-for="item in filteredActiveTaxes"
                                :key="item.id"
                                :label="taxLabel(item)"
                                :value="item.id"
                                :disabled="!isEinvoicingCompliant(item)"
                            />
                        </CompassSelect>
                    </CompassFormItem>
                </template>
            </Card>
            <div class="vat-settings__columns--right">
                <Card>
                    <div class="vat-settings__card-header">
                        <Heading
                            :labelTitle="$t.__('settings.taxes.VatSettings.ecotax.title')"
                            variant="h2"
                            :prefixColoredIcon="{ icon: 'leaf', color: 'brand' }"
                        />
                        <CompassButton
                            v-if="!isLoading && !ecoTaxUseRowVat()"
                            :label="$t.__('settings.taxes.VatSettings.ecotax.button')"
                            type="secondary"
                            leftIcon="settings"
                            @click="openEcoTaxDialog"
                        />
                    </div>
                    <VatSettingsLoader v-if="isLoading" :lines="1" />
                    <CompassSwitch
                        v-else
                        v-model="taxesSettings.use_ecotax"
                        class="vat-settings__switch"
                        orientation="left"
                        :label="$t.__('settings.taxes.VatSettings.ecotax.switch.label')"
                        @change="handleSettingsUpdate"
                    />
                </Card>
                <Card>
                    <Heading
                        :labelTitle="$t.__('settings.taxes.VatSettings.discount.title')"
                        variant="h2"
                        :prefixColoredIcon="{ icon: 'rosette-discount-check', color: 'brand' }"
                    />
                    <VatSettingsLoader v-if="isLoading" />
                    <template v-else>
                        <CompassSwitch
                            v-model="taxesSettings.use_bonuses"
                            class="vat-settings__switch"
                            orientation="left"
                            :label="$t.__('settings.taxes.VatSettings.discount.switch.label')"
                            @change="handleSettingsUpdate"
                        />
                        <CompassFormItem
                            :label="$t.__('settings.taxes.VatSettings.discount.default_discounts')"
                            class="vat-settings__discount-form-item"
                        >
                            <CompassInputUnit
                                v-model="taxesSettings.discount.value"
                                v-model:unit="taxesSettings.discount.type"
                                class="vat-settings__input-unit"
                                :options="discountOptions"
                                type="number"
                                :min="0"
                                @select="handleSettingsUpdate"
                                @update:model-value="handleSettingsUpdate"
                            />
                        </CompassFormItem>
                        <CompassSwitch
                            v-model="taxesSettings.discount.by_rows"
                            class="vat-settings__switch"
                            orientation="left"
                            :label="$t.__('settings.taxes.VatSettings.discount.discount_per_row')"
                            @change="handleSettingsUpdate"
                        />
                    </template>
                </Card>
            </div>
        </div>
        <Card>
            <div class="vat-settings__card-header">
                <Heading
                    :labelTitle="$t.__('settings.taxes.VatSettings.rates.title')"
                    variant="h2"
                    :prefixColoredIcon="{ icon: 'list-details', color: 'brand' }"
                />
                <CompassButton
                    v-if="!isLoading"
                    :label="$t.__('settings.taxes.VatSettings.rates.button')"
                    @click="openRateCategoryDialog"
                />
            </div>
            <RateCategoryTable
                v-if="!isLoading"
                :rateCategories="rateCategories"
                :metadata="rateCategoriesMetadata"
                :taxes="taxes"
                @delete-rate-category="deleteCategory"
                @edit-rate-category="editCategory"
                @change-default-rate="changeDefaultRate"
            />
        </Card>
    </div>
</template>
<script>
import Card from 'vcomponents/common/card/Card.vue';
import Heading from 'vcomponents/common/Heading/Heading.vue';
import VatSettingsLoader from 'vcomponents/settings/taxes/VatSettingsLoader.vue';
import RateCategoryTable from 'vcomponents/settings/taxes/popup/RateCategoryTable.vue';

import vatStore from 'stores/taxes/vatStore';
import rateCategoriesStore from 'stores/rateCategories/rateCategoriesStore';

import { createNamespacedHelpers } from 'vuex';
import { debounce } from 'lodash';
import { useTax } from 'composables/useTax';

import Feature from 'Services/Feature';
import { mapActions, mapState } from 'pinia';
import { useLanguagesStore } from 'piniaStores/languages/languagesStore';
const { mapState: corpMapState } = createNamespacedHelpers('corp');
const { mapActions: popupPanelMapActions } = createNamespacedHelpers('popupPanel');
const { mapGetters: vatMapGetters, mapActions: vatMapActions } =
    createNamespacedHelpers('vatStore');
const { mapGetters: rateCategoriesMapGetters, mapActions: rateCategoriesMapActions } =
    createNamespacedHelpers('rateCategoriesStore');
const { taxLabel, isEinvoicingCompliant } = useTax();
export default {
    components: {
        Card,
        Heading,
        VatSettingsLoader,
        RateCategoryTable,
    },
    data() {
        return {
            isLoading: true,
            vatModes: [
                { value: 'debit', label: this.$l10n.__('TVA sur les débits') },
                { value: 'collection', label: this.$l10n.__('TVA sur les encaissements') },
            ],
            vatMention: '',
            discountOptions: [
                {
                    label: '%',
                    value: 'percent',
                },
                {
                    label: this.numformat?.currencysymbol ?? '€',
                    value: 'amount',
                },
            ],
            controller: new AbortController(),
        };
    },
    computed: {
        ...corpMapState(['numformat']),
        ...vatMapGetters(['taxes', 'taxesSettings', 'listNoEcoTaxes', 'listEcoTaxes']),
        ...rateCategoriesMapGetters(['rateCategories', 'rateCategoriesMetadata']),
        ...mapState(useLanguagesStore, ['languages']),
        filteredActiveTaxes() {
            return this.listNoEcoTaxes.filter((tax) => tax.is_active);
        },
    },
    created() {
        this.$store.registerModule('vatStore', vatStore);
        this.$store.registerModule('rateCategoriesStore', rateCategoriesStore);

        Promise.all([
            this.fetchLanguages(),
            this.fetchTaxesSettings(),
            this.fetchTaxes(),
            this.fetchRateCategories(),
            this.fetchRateCategoriesMetadata(),
        ])
            .catch(() => {
                this.$pushToast({
                    type: 'error',
                    text: this.$t.__('settings.taxes.VatSettings.vat.fetch_error'),
                });
            })
            .finally(() => {
                this.isLoading = false;
                this.$nextTick(() => {
                    // we have to call the method again due to the v-if on the template which manages the loading state
                    window.Quota.checkQuotas(['rate_categories']);
                });
            });
    },
    methods: {
        ...vatMapActions(['fetchTaxes', 'fetchTaxesSettings', 'updateTaxesSettings']),
        ...popupPanelMapActions(['close', 'open']),
        ...rateCategoriesMapActions([
            'fetchRateCategories',
            'fetchRateCategoriesMetadata',
            'deleteRateCategory',
            'updateRateCategory',
        ]),
        isEinvoicingCompliant,
        taxLabel,
        ecoTaxUseRowVat() {
            return Feature.isEnabled('ecotax-use-row-vat-rate');
        },
        forceVatMention() {
            return Feature.isEnabled('force-vat-mention');
        },
        ...mapActions(useLanguagesStore, ['fetchLanguages']),
        handleSettingsUpdate(fetchRateCategories = false) {
            if (this.taxesSettings.discount.value === '') {
                this.taxesSettings.discount.value = '0';
            }
            debounce(() => {
                this.controller.abort();
                this.controller = new AbortController();
                this.updateTaxesSettings(this.controller.signal)
                    .then(() => {
                        if (fetchRateCategories) {
                            this.fetchRateCategories();
                        }
                    })
                    .catch(() => {
                        this.$pushToast({
                            type: 'error',
                            text: this.$t.__('settings.taxes.VatSettings.vat.update_error'),
                        });
                    });
            }, 300)();
        },
        openVatRateDialog() {
            this.open({
                key: 'vatRateDialog',
                props: {},
            });
        },
        openEcoTaxDialog() {
            this.open({
                key: 'ecoTaxDialog',
                props: {
                    languages: this.languages,
                    ecoTaxes: this.listEcoTaxes,
                },
            });
        },
        deleteCategory(id) {
            this.deleteRateCategory(id)
                .then(() => {
                    this.$pushToast({
                        type: 'success',
                        text: this.$t.__('settings.taxes.VatSettings.rates.delete.success'),
                    });
                })
                .catch(() => {
                    this.$pushToast({
                        type: 'error',
                        text: this.$t.__('settings.taxes.VatSettings.rates.delete.error'),
                    });
                });
        },
        openRateCategoryDialog() {
            this.open({
                key: 'rateCategoryDialog',
                props: {
                    rateCategory: undefined,
                },
            });
        },
        editCategory(category) {
            this.open({
                key: 'rateCategoryDialog',
                props: {
                    rateCategory: category,
                },
            });
        },
        changeDefaultRate(rate) {
            this.taxesSettings.rate_category_id = rate.id;
            this.handleSettingsUpdate(true);
        },
    },
};
</script>

<style lang="scss" scoped>
.vat-settings {
    &__columns {
        align-items: start;
        display: flex;
        gap: var(--spacing-4);
        margin-bottom: 24px;

        @media screen and (max-width: 1199px) {
            align-items: stretch;
            flex-direction: column;
        }

        :deep() {
            .card-container {
                flex: 0 1 50%;
            }
        }

        &--right {
            flex: 0 1 50%;
        }
    }

    &__card-header {
        align-items: start;
        display: flex;
        justify-content: space-between;
    }

    &__switch {
        display: inline-flex;
    }

    &__form-item {
        margin: 0 0 var(--spacing-4);

        &--bordered {
            border-bottom: 1px solid var(--colors-common-border-lighter);
            display: flex;
            flex-direction: column;
            gap: var(--spacing-1);
            margin: 0 0 var(--spacing-4);
            padding: 0 0 var(--spacing-4);
        }
    }

    &__select,
    &__input-unit {
        max-width: 220px;
    }

    &__discount-form-item {
        margin: var(--spacing-4) 0 var(--spacing-1);
    }
}
</style>
